import * as React from "react";
import { ChevronDown, FileText } from "lucide-react";

import { formatDate } from "./lib/date";
import { sourceDate } from "./lib/sources";
import { headingTag, useHeadingLevel, type HeadingLevel } from "./lib/heading";
import { FOCUS_RING, FOCUS_RING_INSET_AFTER } from "./lib/focus";
import { cn } from "./lib/utils";
import { MachineScopeSlot } from "./MachineScopeMarker";
import { SourceCitations, SourceDensityContext, SourceGroupContext, type SourceCardDensity } from "./SourceCard";
import { Icon } from "./ui/icon";
import { LIST_ROW_HOVER } from "./ui/list-item";

/**
 * v2 SourceList, SourceSection, SourceGroup (redesign phase 5, round 10,
 * #455): the frame around SourceCard rows (breaks #142, #143). Presentational:
 * which references go in which section, how documents group and when a list
 * turns compact (`COMPACT_CASE_LIST_THRESHOLD`) stay the app's (04, "Sources").
 *
 * - SourceList: a `<ul>` of rows, no lines between them: the rows' padding and
 *   the titles separate them, and lines between short rows read as a table; `density`
 *   reaches its cards. Flush in a compact Card (`CardContent flush`): the rows
 *   pad 12, the compact card's inset.
 * - SourceSection: a type section ("Dokumente", "Ähnliche Servicefälle") with
 *   the library's small label (12 Semibold `--ink-muted`, sentence case) and
 *   the count. No separators between sections: spacing does it.
 * - SourceGroup: one cited document, one passage or several. Its header is a
 *   row on the same grid as a SourceCard (file icon, title and date, machine
 *   marker), so every title in a list starts in one column; the cover, when
 *   there is one, ends the header row and shares its hover and click. Closed by
 *   default, so a list shows every document's title at a glance. As on a
 *   SourceCard, a click on the row shows the passages and the title (a link
 *   above the row) opens the document. `open` lets the app open it, e.g. for a
 *   citation the reader clicked; the reader can still close it. `citations` shows
 *   the answer's marker numbers of all its passages after the title. Its passages are
 *   SourceCards titled "S. 12" under the title column; they drop the type icon and are
 *   named after the document.
 */

export interface SourceListProps extends React.HTMLAttributes<HTMLUListElement> {
  density?: SourceCardDensity;
}

export const SourceList = React.forwardRef<HTMLUListElement, SourceListProps>(function SourceList(
  { density = "default", className, ...props },
  ref,
) {
  return (
    <SourceDensityContext.Provider value={density}>
      <ul ref={ref} role="list" className={cn("flex flex-col", className)} {...props} />
    </SourceDensityContext.Provider>
  );
});

export interface SourceSectionProps extends Omit<React.HTMLAttributes<HTMLElement>, "title"> {
  title: string;
  /** Shown after the title, e.g. the number of cases. */
  count?: number;
  /** Default: the context level (h3 on its own). */
  headingLevel?: HeadingLevel;
}

export function SourceSection({ title, count, headingLevel, className, children, ...props }: SourceSectionProps) {
  const id = React.useId();
  const Heading = headingTag(useHeadingLevel(headingLevel, 3));
  return (
    <section aria-labelledby={id} className={cn("flex flex-col gap-1", className)} {...props}>
      <Heading id={id} className="px-3 font-sans text-caption font-semibold text-ink-muted">
        {title}
        {count != null && (
          <>
            {" "}
            <span className="font-normal tabular-nums">{count}</span>
          </>
        )}
      </Heading>
      {children}
    </section>
  );
}

export interface SourceGroupLabels {
  /** `(date) => "Stand: " + date`, as SourceCard. */
  date: (formattedDate: string) => string;
  /** `(n) => n + " Stellen"`: how many passages a closed group holds. */
  count: (count: number) => string;
  /** Name of the chevron that shows or hides the passages: "Stellen anzeigen". */
  passagesToggle: string;
  /** Hidden after a linked title: "öffnet in neuem Tab", as SourceCard. */
  newTab: string;
}

export interface SourceGroupProps extends Omit<React.HTMLAttributes<HTMLLIElement>, "title" | "children"> {
  /** The document's title. */
  title: string;
  /** Number of passages (usually the children's count). */
  count: number;
  date?: string | Date | null;
  machineScope?: string | null;
  /** URL of the document's cover thumbnail, beside the group so a manual is recognised at a glance. */
  cover?: string | null;
  /** Opens the document in a new tab: the title is the link, as a SourceCard's. */
  href?: string;
  /** Without `href`: the in-app open, the title as a button; with it, the link's click handler. */
  onOpen?: (event: React.MouseEvent<HTMLElement>) => void;
  /** Open on first render. Default closed: a list shows every document's title at a glance. */
  defaultOpen?: boolean;
  /** Controlled open state; uncontrolled (`defaultOpen`) when not given. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** The answer's citation numbers of its passages ("[2]" in the text). */
  citations?: readonly number[];
  /** The passages: SourceCards titled `labels.page(n)` by the caller. */
  children: React.ReactNode;
  labels: SourceGroupLabels;
}

export function SourceGroup({
  title,
  count,
  date,
  machineScope,
  cover,
  href,
  onOpen,
  defaultOpen = false,
  open: openProp,
  onOpenChange,
  citations,
  children,
  labels,
  className,
  ...props
}: SourceGroupProps) {
  const [chosen, setChosen] = React.useState(defaultOpen);
  const open = openProp ?? chosen;
  const setOpen = (next: boolean) => {
    setChosen(next);
    onOpenChange?.(next);
  };
  const passagesId = React.useId();
  const content = sourceDate(date);
  const groupContext = React.useMemo(() => ({ title }), [title]);
  const meta = [content ? labels.date(formatDate(content)) : null, labels.count(count)].filter(Boolean).join(" · ");
  // The row expands wherever it is clicked (the chevron's overlay covers it); the title,
  // above that overlay, is the link that opens the document.
  const titleClass = cn(
    "min-w-0 text-left font-sans text-caption font-semibold text-ink outline-hidden wrap-break-word",
    (href || onOpen) && cn("relative z-10 cursor-pointer rounded-sm hover:underline underline-offset-2", FOCUS_RING),
  );
  return (
    <li data-slot="source-group" className={className} {...props}>
      <div className="flex min-w-0 flex-col">
        {/* The SourceCard grid: px-3, a 12 icon in a 16 high line, gap 8. The chevron's
            ::after covers the row; the title and the marker sit above it. */}
        <div className={cn("group/item relative flex items-start gap-2 px-3 py-1.5", LIST_ROW_HOVER)}>
          <span className="flex h-4 shrink-0 items-center text-ink-muted">
            <Icon icon={FileText} size={12} />
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <div className="flex items-start gap-1">
              {href ? (
                <a href={href} target="_blank" rel="noreferrer" onClick={onOpen} className={titleClass}>
                  {title} <span className="sr-only">({labels.newTab})</span>
                </a>
              ) : onOpen ? (
                <button type="button" onClick={onOpen} className={titleClass}>
                  {title}
                </button>
              ) : (
                <span className={titleClass}>{title}</span>
              )}
              <SourceCitations numbers={citations} />
              {/* Its ::after covers the row: a click anywhere shows the passages; the title, above it, opens. */}
              <button
                type="button"
                aria-expanded={open}
                aria-controls={passagesId}
                aria-label={labels.passagesToggle}
                onClick={() => setOpen(!open)}
                className={cn(
                  "flex h-4 shrink-0 cursor-pointer items-center text-ink-muted outline-hidden group-hover/item:text-ink",
                  "after:absolute after:inset-0",
                  FOCUS_RING_INSET_AFTER,
                )}
              >
                <Icon
                  icon={ChevronDown}
                  size={12}
                  className={cn("transition-transform duration-150 motion-reduce:transition-none", open && "rotate-180")}
                />
              </button>
            </div>
            <span className="font-sans text-caption text-ink-muted tabular-nums">{meta}</span>
          </div>
          <span className="relative z-10 flex h-4 shrink-0 items-center">
            <MachineScopeSlot labels={{ tooltip: machineScope }} size={12} />
          </span>
          {/* Part of the row: under the toggle's overlay, so it shares the hover and a click
              on it expands the group. */}
          {cover && (
            <img
              src={cover}
              alt=""
              loading="lazy"
              className="h-13 w-10 shrink-0 rounded-sm border border-border bg-white object-contain object-top"
            />
          )}
        </div>
        {/* Passages under the title column: their own px-3 plus 20 is the header's 12 + 12 + 8. */}
        <div id={passagesId} hidden={!open}>
          {open && (
            <SourceGroupContext.Provider value={groupContext}>
              <SourceList className="pl-5">{children}</SourceList>
            </SourceGroupContext.Provider>
          )}
        </div>
      </div>
    </li>
  );
}
